import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import "../lib/i18n"; // Initialize i18n
import { useUser } from "@/hooks/useUser";
import { LanguagePreference, UserType } from "@/types/api";

type Language = "en" | "es" | "af";

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isReady: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: React.ReactNode;
  user: UserType | null;
}

const LANGUAGE_STORAGE_KEY = "userLanguagePreference";

// Detect browser language
const detectBrowserLanguage = (): Language => {
  if (typeof window === "undefined") return "en";
  const browserLang = navigator.language || navigator.languages?.[0] || "en";
  const langCode = browserLang.split("-")[0].toLowerCase();
  if (langCode === "es") return "es";
  if (langCode === "af") return "af";
  return "en";
};

// Convert backend enum to i18n language code
const toI18nLanguage = (pref?: LanguagePreference): Language => {
  if (!pref) return "en";
  return pref.toLowerCase() as Language;
};

// Convert i18n language code to backend enum
const toBackendLanguage = (lang: Language): LanguagePreference => {
  return lang.toUpperCase() as LanguagePreference;
};

// Get initial language - localStorage is source of truth
const getInitialLanguage = (user: UserType | null): Language => {
  if (typeof window === "undefined") return "en";

  // Check localStorage first (source of truth for this device)
  const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language;
  if (saved) return saved;

  // No localStorage - check backend (fresh login or new device)
  if (user?.languagePreference) {
    const lang = toI18nLanguage(user.languagePreference);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    return lang;
  }

  // Detect from browser
  const browserLang = detectBrowserLanguage();
  localStorage.setItem(LANGUAGE_STORAGE_KEY, browserLang);
  return browserLang;
};

export function I18nProvider({ children, user }: I18nProviderProps) {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { updateUserDetailsMutation } = useUser(user?.id?.toString() || "");
  const [language, setLanguageState] = useState<Language>(() =>
    getInitialLanguage(user)
  );

  // Sync i18n library with language state
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  // Save language to backend for first-time users
  useEffect(() => {
    if (user?.id && !user?.languagePreference) {
      updateUserDetailsMutation.mutate({
        userData: { languagePreference: toBackendLanguage(language) },
        showSuccessSnackbar: false,
      });
    }
  }, [user?.id, user?.languagePreference, language, updateUserDetailsMutation]);

  const setLanguage = (lang: Language) => {
    // Update everything
    setLanguageState(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);

    // Save to backend and invalidate all queries to refetch localized data
    if (user?.id) {
      updateUserDetailsMutation.mutate(
        {
          userData: { languagePreference: toBackendLanguage(lang) },
          showSuccessSnackbar: false,
        },
        {
          onSuccess: () => {
            // Invalidate all queries to refetch with new language preference
            queryClient.invalidateQueries();
          },
        }
      );
    }
  };

  // Context is ready when user data has loaded (or confirmed no user)
  const isReady = user !== null || typeof window === "undefined";

  const value = useMemo(
    () => ({ language, setLanguage, isReady }),
    [language, isReady]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18nContext() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error("useI18nContext must be used within an I18nProvider");
  }
  return context;
}
